import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { SecondaryLink } from '@bibliocommons/base-links';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';

import ContextMenu from 'app/components/admin/browse/shared/ContextMenu';
import ReOrderButtons from 'app/components/admin/browse/shared/ReOrderButtons';
import { VALIDATION_ERROR_MESSAGE } from 'app/constants/admin/browse/home';
import { makeEmptyListUrl } from 'app/helpers/admin/browse/home';
import { listUrlShape } from 'app/shapes/admin/browse/home';
import EditorListUrl from './EditorListUrl';
import TableWrapper from './TableWrapper';

import './EditorListUrls.scss';

const getNumRows = (listUrls, numRowsMin, numRowsMax) => {
  const numListUrls = listUrls.filter(Boolean).length;

  return Math.max(Math.min(numListUrls + 1, numRowsMax), numRowsMin);
};

const isDuplicateUrl = (index, newListUrl, currentRows) => {
  const { url: candidateUrl } = newListUrl;
  const isDuplicate = currentRows.some((row, nextIndex) => {
    if (nextIndex === index) {
      return false;
    }
    const { listUrl } = row;
    const { url } = listUrl;

    return url === candidateUrl;
  });

  return isDuplicate;
};

const EditorListUrls = ({ listUrls, listUrlErrors, numRowsMax, numRowsMin, onChange }) => {
  const numRows = getNumRows(listUrls, numRowsMin, numRowsMax);
  const initialRows = [...Array(numRows)].fill(null).map((_, index) => {
    const listUrl = listUrls[index] || makeEmptyListUrl();

    return {
      errorMessage: '',
      isEditing: !listUrl.url,
      listUrl
    };
  });

  const [state, setState] = useState({
    rows: initialRows,
    timestampLastSave: Date.now()
  });

  const { rows, timestampLastSave } = state;

  useEffect(() => {
    const newListUrls = rows.map(row => row.listUrl).filter(listUrl => Boolean(listUrl.url));

    onChange(newListUrls);

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [timestampLastSave]);

  const canMoveDown = index => {
    return Boolean(rows[index + 1]?.listUrl?.url);
  };

  const handleCancelClick = index => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = [...prevRows];

      newRows[index].isEditing = false;

      return {
        ...prevState,
        rows: newRows
      };
    });
  };

  const handleEditClick = index => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = [...prevRows];

      newRows[index].isEditing = true;

      return {
        ...prevState,
        rows: newRows
      };
    });
  };

  const handleUrlChange = (index, newListUrl) => {
    setState(prevState => {
      const { rows: prevRows } = prevState;
      const isDuplicate = isDuplicateUrl(index, newListUrl, prevRows);
      if (isDuplicate) {
        const newRows = prevRows.map((row, nextIndex) => {
          if (nextIndex === index) {
            return {
              errorMessage: VALIDATION_ERROR_MESSAGE.DUPLICATE_LIST_URL,
              isEditing: true,
              listUrl: row.listUrl
            };
          }

          return row;
        });

        return {
          ...prevState,
          rows: newRows
        };
      }

      const newRows = prevRows
        .map((row, nextIndex) => {
          if (nextIndex === index) {
            return {
              errorMessage: '',
              isEditing: false,
              listUrl: newListUrl
            };
          }

          return row;
        })
        .filter(row => Boolean(row.listUrl.url));

      const newNumRows = getNumRows(
        newRows.map(row => row.listUrl),
        numRowsMin,
        numRowsMax
      );

      while (newRows.length < newNumRows) {
        newRows.push({
          errorMessage: '',
          isEditing: true,
          listUrl: makeEmptyListUrl()
        });
      }

      return {
        ...prevState,
        rows: newRows,
        timestampLastSave: Date.now()
      };
    });
  };

  const handleUrlDelete = index => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = prevRows.filter((row, nextIndex) => {
        return Boolean(row.listUrl?.url) && nextIndex !== index;
      });

      const newNumRows = getNumRows(
        newRows.map(row => row.listUrl),
        numRowsMin,
        numRowsMax
      );

      while (newRows.length < newNumRows) {
        newRows.push({
          errorMessage: '',
          isEditing: true,
          listUrl: makeEmptyListUrl()
        });
      }

      return {
        ...prevState,
        rows: newRows,
        timestampLastSave: Date.now()
      };
    });
  };

  const handleUrlsReOrder = newRows => {
    setState({
      rows: newRows,
      timestampLastSave: Date.now()
    });
  };

  return (
    <div className="cp-admin-browse-home-editor-list-urls">
      <TableWrapper>
        <Table>
          <TableHeader>
            <TableHeading>List title</TableHeading>
            <TableHeading colSpan={2}>List URL</TableHeading>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const { errorMessage: rowErrorMessage, isEditing, listUrl } = row;

              const { listUrlId, title, url } = listUrl;
              const errorMessage = rowErrorMessage || listUrlErrors?.[index] || '';
              const confirmDeleteModalMessage = `Are you sure you want to delete the '${title}' list URL?`;
              const rowKey = listUrlId;
              const urlLinkKey = `${rowKey}_urlLink`;

              return (
                <TableRow key={rowKey}>
                  {isEditing ? (
                    <TableCell colSpan={3}>
                      <EditorListUrl
                        errorMessage={errorMessage}
                        listUrl={listUrl}
                        onCancel={() => handleCancelClick(index)}
                        onChange={newListUrl => {
                          handleUrlChange(index, newListUrl);
                        }}
                      />
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>{title}</TableCell>
                      <TableCell>
                        <SecondaryLink dataKey={urlLinkKey} href={url} target="_blank">
                          {url}
                        </SecondaryLink>
                      </TableCell>
                      <TableCell className="cell-actions">
                        <div className="actions-wrapper">
                          <ReOrderButtons
                            canMoveDown={canMoveDown}
                            index={index}
                            items={rows}
                            onChange={handleUrlsReOrder}
                          />
                          <ContextMenu
                            className="action-context-menu"
                            confirmDeleteModalHeading="Delete list URL"
                            confirmDeleteModalMessage={confirmDeleteModalMessage}
                            onDeleteClick={() => handleUrlDelete(index)}
                            onEditClick={() => handleEditClick(index)}
                          />
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>
    </div>
  );
};

EditorListUrls.propTypes = {
  listUrls: PropTypes.arrayOf(listUrlShape).isRequired,
  listUrlErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  numRowsMax: PropTypes.number.isRequired,
  numRowsMin: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};

export default EditorListUrls;
